import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api, type Id } from "../lib/api";
import { Button, Card, Icon, JurisBadge, TierBadge, cx } from "../components/ui";
import { caughtErrorMessage } from "../domain/errors";
import { availableCoverageLegislation, subscriptionLegislationIds } from "../domain/subscriptions";
import { formatNullableAuDate } from "../domain/time";
import {
  STAFF_ROLE_OPTIONS,
  emptyStaffAdminSubmissionState,
  emptyStaffCompanyCreateFormState,
  emptyStaffUserCreateFormState,
  nextStaffAdminSubmissionState,
  nextStaffCompanyCreateFormState,
  nextStaffUserCreateFormState,
  staffAvailableCoverageLimit,
  staffCompanyCreateFormSubmittedState,
  staffAdminSubmissionNoticeToneClass,
  staffCompanyCreateFormView,
  staffRoleDisplayName,
  staffUserCreateFormSubmittedState,
  staffUserCreateFormView,
  subscriptionTypeFromInput,
  type StaffCompanyCreateFormAction,
  type StaffUserCreateFormAction,
} from "../domain/staffAdmin";
import type { SubscriptionType } from "../../convex/model/enums";

type Detail = NonNullable<FunctionReturnType<typeof api.admin.companyDetail>>;

export function StaffAdmin() {
  const companiesQuery = useQuery(api.admin.overview, {});
  const companies = useMemo(() => companiesQuery ?? [], [companiesQuery]);
  const plans = useQuery(api.plans.list) ?? [];
  const legislationQuery = useQuery(api.legislation.list, { status: "active" });
  const legislation = useMemo(() => legislationQuery ?? [], [legislationQuery]);
  const bundles = useQuery(api.bundles.list, {}) ?? [];

  const createCompany = useMutation(api.admin.createCompany);
  const createUser = useMutation(api.admin.createUser);
  const setUserActive = useMutation(api.admin.setUserActive);
  const setSubscription = useMutation(api.admin.setCompanySubscription);
  const applyBundle = useMutation(api.admin.applyBundleToCompany);

  const [selectedId, setSelectedId] = useState<Id<"companies"> | null>(null);
  const detail = useQuery(api.admin.companyDetail, selectedId !== null ? { companyId: selectedId } : "skip");

  useEffect(() => {
    const firstCompany = companies[0];
    if (selectedId === null && firstCompany !== undefined) setSelectedId(firstCompany._id);
  }, [companies, selectedId]);

  const [companyDraft, setCompanyDraft] = useState(() => emptyStaffCompanyCreateFormState());
  const [companySubmission, setCompanySubmission] = useState(() => emptyStaffAdminSubmissionState());
  const [userDraft, setUserDraft] = useState(() => emptyStaffUserCreateFormState());
  const [userSubmission, setUserSubmission] = useState(() => emptyStaffAdminSubmissionState());

  const [lawSearch, setLawSearch] = useState("");
  const [subType, setSubType] = useState<SubscriptionType>("standard");

  const subscribedIds = useMemo(
    () => subscriptionLegislationIds(detail?.subscriptions ?? []),
    [detail],
  );
  const available = useMemo(
    () => availableCoverageLegislation({ legislation, subscribedIds, query: lawSearch }),
    [lawSearch, legislation, subscribedIds],
  );

  const companyFormView = useMemo(
    () => staffCompanyCreateFormView(companyDraft),
    [companyDraft],
  );
  const userFormView = useMemo(
    () => staffUserCreateFormView({ draft: userDraft, selectedCompany: selectedId !== null }),
    [selectedId, userDraft],
  );
  const availableLimit = staffAvailableCoverageLimit(lawSearch);

  const updateCompanyDraft = (action: StaffCompanyCreateFormAction) => {
    setCompanyDraft((current) => nextStaffCompanyCreateFormState(current, action));
  };

  const updateUserDraft = (action: StaffUserCreateFormAction) => {
    setUserDraft((current) => nextStaffUserCreateFormState(current, action));
  };

  const submitCompany = async () => {
    if (!companyFormView.prepared.ok || companySubmission.busy) return;
    setCompanySubmission((current) => nextStaffAdminSubmissionState(current, { type: "started" }));
    try {
      const id = await createCompany(companyFormView.prepared.value);
      setSelectedId(id);
      setCompanyDraft((current) => staffCompanyCreateFormSubmittedState(current));
      setCompanySubmission((current) => nextStaffAdminSubmissionState(current, {
        type: "succeeded",
        message: "Company created.",
      }));
    } catch (e) {
      setCompanySubmission((current) => nextStaffAdminSubmissionState(current, {
        type: "failed",
        error: caughtErrorMessage(e, "Could not create company."),
      }));
    }
  };

  const submitUser = async () => {
    if (selectedId === null || !userFormView.prepared.ok || userSubmission.busy) return;
    setUserSubmission((current) => nextStaffAdminSubmissionState(current, { type: "started" }));
    try {
      await createUser({
        companyId: selectedId,
        ...userFormView.prepared.value,
      });
      setUserDraft((current) => staffUserCreateFormSubmittedState(current));
      setUserSubmission((current) => nextStaffAdminSubmissionState(current, {
        type: "succeeded",
        message: "User record created.",
      }));
    } catch (e) {
      setUserSubmission((current) => nextStaffAdminSubmissionState(current, {
        type: "failed",
        error: caughtErrorMessage(e, "Could not create user record."),
      }));
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-6 py-7 lg:px-9">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl font-semibold text-slate-900">Staff admin</h1>
          <p className="mt-1 text-sm text-slate-500">Create clients, users, and company-level subscription coverage.</p>
        </div>
        <div className="text-[12.5px] text-slate-400">{companies.length} companies</div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[22rem_1fr]">
        <div className="space-y-5">
          <Card className="overflow-hidden">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="text-[14px] font-semibold text-slate-900">Companies</h2>
            </div>
            <div className="max-h-[520px] divide-y divide-slate-100 overflow-y-auto">
              {companies.map((company) => (
                <button
                  key={company._id}
                  onClick={() => setSelectedId(company._id)}
                  className={cx(
                    "block w-full px-4 py-3 text-left transition hover:bg-slate-50",
                    selectedId === company._id && "bg-[var(--accent-soft)]",
                  )}
                >
                  <div className="truncate text-[13.5px] font-semibold text-slate-800">{company.name}</div>
                  <div className="mt-1 flex flex-wrap gap-2 text-[11.5px] text-slate-500">
                    <span>{company.status}</span>
                    <span>{company.subscriptionCount} laws</span>
                    <span>{company.userCount} users</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          <Card className="p-4">
            <h2 className="mb-3 text-[14px] font-semibold text-slate-900">Add company</h2>
            <div className="space-y-2">
              <input aria-label="Company name" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Company name" value={companyDraft.name} onChange={(e) => updateCompanyDraft({ type: "nameInput", value: e.target.value })} />
              <input aria-label="Company website" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Website" value={companyDraft.website} onChange={(e) => updateCompanyDraft({ type: "websiteInput", value: e.target.value })} />
              <input aria-label="Company logo URL" className="w-full rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Logo URL" value={companyDraft.logoUrl} onChange={(e) => updateCompanyDraft({ type: "logoUrlInput", value: e.target.value })} />
              <div className="grid grid-cols-[1fr_5rem] gap-2">
                <select
                  aria-label="Company plan"
                  className="rounded border border-slate-300 px-3 py-2 text-sm"
                  value={companyDraft.activePlanId ?? ""}
                  onChange={(e) => updateCompanyDraft({ type: "activePlanInput", plans, value: e.target.value })}
                >
                  <option value="">No plan</option>
                  {plans.map((plan) => <option key={plan._id} value={plan._id}>{plan.name}</option>)}
                </select>
                <input aria-label="Trial duration in days" className="rounded border border-slate-300 px-3 py-2 text-sm" value={companyDraft.trialDays} onChange={(e) => updateCompanyDraft({ type: "trialDaysInput", value: e.target.value })} />
              </div>
              {companyFormView.problem !== null && <div className="text-[12.5px] text-amber-700">{companyFormView.problem}</div>}
              <Button className="w-full" icon="plus" disabled={!companyFormView.canSubmit || companySubmission.busy} onClick={submitCompany}>
                {companySubmission.busy ? "Creating..." : "Create company"}
              </Button>
              {companySubmission.notice !== null && <div className={cx("text-[12.5px]", staffAdminSubmissionNoticeToneClass(companySubmission.notice))}>{companySubmission.notice.text}</div>}
            </div>
          </Card>
        </div>

        {detail === undefined ? (
          <p className="text-slate-500">Loading company...</p>
        ) : detail === null ? (
          <Card className="px-5 py-12 text-center text-sm text-slate-400">Select a company.</Card>
        ) : (
          <div className="space-y-5">
            <CompanyHeader detail={detail} />

            <div className="grid gap-5 lg:grid-cols-2">
              <Card className="overflow-hidden">
                <div className="border-b border-slate-100 px-5 py-3.5">
                  <h2 className="text-[14px] font-semibold text-slate-900">Users</h2>
                  <p className="mt-0.5 text-[12.5px] text-slate-400">App user records for this company. Clerk invitations can be connected later.</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {detail.users.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-slate-400">No users yet.</div>
                  ) : detail.users.map((user) => (
                    <div key={user._id} className="flex items-center gap-3 px-5 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13.5px] font-semibold text-slate-800">{user.name ?? user.email}</div>
                        <div className="mt-0.5 text-[12px] text-slate-400">{user.email} / {user.role} / expires {formatNullableAuDate(user.accountExpiresAt, "-")}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setUserActive({ userId: user._id, active: !user.active })}>
                        {user.active ? "Disable" : "Enable"}
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-5">
                <h2 className="mb-3 text-[14px] font-semibold text-slate-900">Add user</h2>
                <div className="grid gap-2">
                  <input aria-label="User email" className="rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Email" value={userDraft.email} onChange={(e) => updateUserDraft({ type: "emailInput", value: e.target.value })} />
                  <input aria-label="User name" className="rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Name" value={userDraft.name} onChange={(e) => updateUserDraft({ type: "nameInput", value: e.target.value })} />
                  <input aria-label="Username" className="rounded border border-slate-300 px-3 py-2 text-sm" placeholder="Username" value={userDraft.username} onChange={(e) => updateUserDraft({ type: "usernameInput", value: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <select aria-label="User role" className="rounded border border-slate-300 px-3 py-2 text-sm" value={userDraft.role} onChange={(e) => updateUserDraft({ type: "roleInput", value: e.target.value })}>
                      {STAFF_ROLE_OPTIONS.map((role) => <option key={role.value} value={role.value}>{staffRoleDisplayName(role.value)}</option>)}
                    </select>
                    <input aria-label="Account expiry date" type="date" className="rounded border border-slate-300 px-3 py-2 text-sm" value={userDraft.accountExpiresOn} onChange={(e) => updateUserDraft({ type: "accountExpiresOnInput", value: e.target.value })} />
                  </div>
                  {userFormView.problem !== null && <div className="text-[12.5px] text-amber-700">{userFormView.problem}</div>}
                  <Button icon="plus" disabled={!userFormView.canSubmit || userSubmission.busy} onClick={submitUser}>
                    {userSubmission.busy ? "Creating..." : "Create user record"}
                  </Button>
                  {userSubmission.notice !== null && <div className={cx("text-[12.5px]", staffAdminSubmissionNoticeToneClass(userSubmission.notice))}>{userSubmission.notice.text}</div>}
                </div>
              </Card>
            </div>

            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
                <div>
                  <h2 className="text-[14px] font-semibold text-slate-900">Company subscriptions</h2>
                  <p className="mt-0.5 text-[12.5px] text-slate-400">Coverage is company-level; users inherit the same legal register.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <select aria-label="Subscription type" className="rounded border border-slate-300 px-2 py-1.5 text-[13px]" value={subType} onChange={(e) => setSubType(subscriptionTypeFromInput(e.target.value, subType))}>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                  </select>
                  {bundles.slice(0, 3).map((bundle) => (
                    <Button key={bundle._id} size="sm" variant="outline" onClick={() => applyBundle({ companyId: detail.company._id, bundleId: bundle._id, subscriptionType: subType })}>
                      Add {bundle.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid gap-0 lg:grid-cols-2">
                <div className="border-b border-slate-100 lg:border-b-0 lg:border-r">
                  <div className="border-b border-slate-100 px-5 py-2.5 text-[12px] font-semibold uppercase tracking-wide text-slate-400">Current coverage</div>
                  <div className="max-h-[390px] divide-y divide-slate-100 overflow-y-auto">
                    {detail.subscriptions.length === 0 ? (
                      <div className="px-5 py-8 text-sm text-slate-400">No subscriptions.</div>
                    ) : detail.subscriptions.map((sub) => (
                      <div key={sub._id} className="flex items-center gap-3 px-5 py-3">
                        {sub.legislation !== null && <JurisBadge code={sub.legislation.jurisdiction} />}
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[13.5px] font-medium text-slate-800">{sub.legislation?.title ?? "Unknown legislation"}</div>
                          <div className="mt-0.5 text-[12px] text-slate-400">via {sub.source}</div>
                        </div>
                        <TierBadge tier={sub.subscriptionType} />
                        <button className="text-[12.5px] font-medium text-red-600 hover:underline" onClick={() => setSubscription({ companyId: detail.company._id, legislationId: sub.legislationId, subscribed: false })}>Remove</button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="border-b border-slate-100 px-5 py-3">
                    <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-slate-200">
                      <Icon name="search" size={14} className="text-slate-400" />
                      <input aria-label="Search laws to add" value={lawSearch} onChange={(e) => setLawSearch(e.target.value)} placeholder="Search laws to add" className="w-full bg-transparent text-[13px] outline-none placeholder:text-slate-400" />
                    </div>
                  </div>
                  <div className="max-h-[390px] divide-y divide-slate-100 overflow-y-auto">
                    {available.slice(0, availableLimit).map((law) => (
                      <div key={law._id} className="flex items-center gap-3 px-5 py-3">
                        <JurisBadge code={law.jurisdiction} />
                        <div className="min-w-0 flex-1 truncate text-[13.5px] text-slate-800">{law.title}</div>
                        <Button size="sm" variant="outline" icon="plus" onClick={() => setSubscription({ companyId: detail.company._id, legislationId: law._id, subscribed: true, subscriptionType: subType })}>Add</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function CompanyHeader({ detail }: { detail: Detail }) {
  const { company } = detail;
  return (
    <Card className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[12.5px] font-medium text-[var(--accent-dark)]">{company.status}</div>
          <h2 className="mt-1 font-serif text-[24px] font-semibold text-slate-950">{company.name}</h2>
          <div className="mt-1 flex flex-wrap gap-3 text-[12.5px] text-slate-500">
            <span>{company.website ?? "No website"}</span>
            <span>trial ends {formatNullableAuDate(company.trialEndsAt, "-")}</span>
            <span>{detail.subscriptions.length} subscriptions</span>
            <span>{detail.users.length} users</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
